import { ruleCategoryLabel } from "./rulesApi";

/**
 * Pure helpers between the add-rule form and the backend's rule draft
 * (`{ type, startIndex, endIndex, diveCount, optional, category, diveDifficulty, height, full,
 * rules }`). Only the fields a rule type supports are ever sent; everything else is left out,
 * because sending `optional: false` to a type without an optional filter is a preview error.
 */

export const WINDOW_MODES = { ALL: "ALL", RANGE: "RANGE" };
export const DIVE_TYPES = {
  ALL: "ALL",
  VOLUNTARY: "VOLUNTARY",
  OPTIONAL: "OPTIONAL",
};

export const MAX_ROUND = 22;

/** Labels for the draft fields, by rule type where a field is overloaded (§8.2). */
const DEFAULT_FIELD_LABELS = {
  diveCount: "Number of dives",
  diveDifficulty: "Degree of difficulty",
  category: "Category",
  height: "Height",
  rules: "Dive bank",
  full: "Match dives by",
};

const FIELD_LABELS_BY_TYPE = {
  DiveTypeCount: { diveCount: "Number of dives in the rounds" },
  CoverHeights: { diveCount: "Dives per height", height: "Number of heights" },
  DiveCoverCategory: { category: "Number of categories" },
  MaxFromList: { diveCount: "Maximum dives from the list" },
  MaxFromCategory: { diveCount: "Maximum per category" },
  MaxFromCategoryOrSkill: { diveCount: "Maximum per category" },
  DiveDifficulty: { diveDifficulty: "Maximum DD per dive" },
  DiveMinDifficulty: { diveDifficulty: "Minimum DD per dive" },
  DiveDifficultyExact: { diveDifficulty: "Fixed DD" },
  DiveDifficultySum: { diveDifficulty: "Maximum DD total" },
  DiveDifficultyMinSum: { diveDifficulty: "Minimum DD total" },
  CustomDD: { diveDifficulty: "DD for the listed dives" },
};

export const fieldLabel = (typeKey, fieldName) =>
  FIELD_LABELS_BY_TYPE[typeKey]?.[fieldName] ||
  DEFAULT_FIELD_LABELS[fieldName] ||
  fieldName;

export const FIELD_HELP = {
  rules: "Space-separated dive numbers, e.g. 101B 103C 5132D",
  full: "Absent means dive number only (SHORT)",
};

/** The form the dialog edits. `values` holds the type's own fields keyed by draft name. */
export const emptyForm = (type) => ({
  windowMode: type?.windowRequired ? WINDOW_MODES.RANGE : WINDOW_MODES.ALL,
  startIndex: "",
  endIndex: "",
  diveType: type?.optionalRequired ? DIVE_TYPES.VOLUNTARY : DIVE_TYPES.ALL,
  values: {},
});

const toText = (value) =>
  value === null || value === undefined ? "" : String(value);

/** Fill the form from a stored rule (RuleAdminDTO) so it can be replaced or edited. */
export const formFromRule = (type, rule) => {
  const form = emptyForm(type);
  if (!rule) return form;
  if (
    type?.supportsWindow &&
    rule.startIndex != null &&
    rule.endIndex != null
  ) {
    form.windowMode = WINDOW_MODES.RANGE;
    form.startIndex = toText(rule.startIndex);
    form.endIndex = toText(rule.endIndex);
  }
  if (type?.supportsOptionalFilter && rule.optional != null) {
    form.diveType = rule.optional ? DIVE_TYPES.OPTIONAL : DIVE_TYPES.VOLUNTARY;
  }
  (type?.fields || []).forEach((spec) => {
    form.values[spec.name] = toText(rule[spec.name]);
  });
  return form;
};

const parseNumber = (text) => {
  const trimmed = toText(text).trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};

const parseInteger = (text) => {
  const n = parseNumber(text);
  return n === null ? null : Math.trunc(n);
};

/** Build the backend draft from the form. Unsupported fields are omitted entirely. */
export const draftFromForm = (type, form) => {
  if (!type) return null;
  const draft = { type: type.key };
  if (type.supportsWindow && form.windowMode === WINDOW_MODES.RANGE) {
    draft.startIndex = parseInteger(form.startIndex);
    draft.endIndex = parseInteger(form.endIndex);
  }
  if (type.supportsOptionalFilter && form.diveType !== DIVE_TYPES.ALL) {
    draft.optional = form.diveType === DIVE_TYPES.OPTIONAL;
  }
  (type.fields || []).forEach((spec) => {
    const raw = form.values?.[spec.name];
    switch (spec.type) {
      case "INTEGER":
      case "CATEGORY":
        draft[spec.name] = parseInteger(raw);
        break;
      case "DECIMAL":
      case "HEIGHT":
        draft[spec.name] = parseNumber(raw);
        break;
      case "FULL_CODE_MODE": {
        const text = toText(raw).trim();
        if (text) draft[spec.name] = text;
        break;
      }
      case "DIVE_BANK":
      default: {
        const text = toText(raw).trim();
        draft[spec.name] = text === "" ? null : text;
      }
    }
  });
  return draft;
};

/** Every required control has a value, so a preview is worth asking for. */
export const isFormComplete = (type, form) => {
  if (!type) return false;
  if (type.supportsWindow && form.windowMode === WINDOW_MODES.RANGE) {
    if (parseInteger(form.startIndex) === null) return false;
    if (parseInteger(form.endIndex) === null) return false;
  }
  if (type.windowRequired && form.windowMode !== WINDOW_MODES.RANGE)
    return false;
  if (type.optionalRequired && form.diveType === DIVE_TYPES.ALL) return false;
  return (type.fields || []).every((spec) => {
    if (!spec.required) return true;
    return toText(form.values?.[spec.name]).trim() !== "";
  });
};

/**
 * Client-side checks that do not need the server: `{ errors: {field: message}, warnings: [] }`.
 * The server re-validates everything; this only shortens the loop for the obvious cases.
 */
export const validateForm = (type, form, totalDives) => {
  const errors = {};
  const warnings = [];
  if (!type) return { errors, warnings };
  if (type.supportsWindow && form.windowMode === WINDOW_MODES.RANGE) {
    const start = parseInteger(form.startIndex);
    const end = parseInteger(form.endIndex);
    if (start !== null && (start < 1 || start > MAX_ROUND)) {
      errors.startIndex = `From round must be between 1 and ${MAX_ROUND}`;
    }
    if (end !== null && (end < 1 || end > MAX_ROUND)) {
      errors.endIndex = `Through round must be between 1 and ${MAX_ROUND}`;
    }
    if (start !== null && end !== null && start > end) {
      errors.endIndex = "Through round must not be before the from round";
    }
    if (
      end !== null &&
      !errors.endIndex &&
      Number.isFinite(totalDives) &&
      totalDives > 0 &&
      end > totalDives
    ) {
      warnings.push(
        `Round ${end} is beyond the ${totalDives} dives of this rule set; the rule is saved but never reaches that round.`,
      );
    }
  }
  (type.fields || []).forEach((spec) => {
    const raw = toText(form.values?.[spec.name]).trim();
    if (raw === "") return;
    if (["INTEGER", "DECIMAL", "CATEGORY", "HEIGHT"].includes(spec.type)) {
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        errors[spec.name] = "Enter a number";
        return;
      }
      if (spec.min != null && n < spec.min) {
        errors[spec.name] = `Must be at least ${spec.min}`;
      } else if (spec.max != null && n > spec.max) {
        errors[spec.name] = `Must be at most ${spec.max}`;
      } else if (
        (spec.type === "INTEGER" || spec.type === "CATEGORY") &&
        !Number.isInteger(n)
      ) {
        errors[spec.name] = "Enter a whole number";
      }
    }
  });
  return { errors, warnings };
};

/** "rounds 1–6 · optional only · full codes" for a rule row's meta line. */
export const scopeMeta = (type, rule) => {
  const parts = [];
  if (type?.supportsWindow) {
    parts.push(
      rule?.startIndex != null && rule?.endIndex != null
        ? rule.startIndex === rule.endIndex
          ? `round ${rule.startIndex}`
          : `rounds ${rule.startIndex}–${rule.endIndex}`
        : "all rounds",
    );
  }
  if (type?.supportsOptionalFilter) {
    if (rule?.optional === true) parts.push("optional only");
    else if (rule?.optional === false) parts.push("voluntary only");
    else parts.push("all dives");
  }
  if (rule?.full && rule.full !== "SHORT") {
    parts.push(rule.full === "FULL" ? "number and position" : "position only");
  }
  return parts.join(" · ");
};

/** Sort order the server applies when it rebuilds a set: catalog default sequence, then id. */
export const defaultOrder = (rows, typesByKey) =>
  [...rows]
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const sa = typesByKey[a.row.type]?.defaultSequence ?? 999;
      const sb = typesByKey[b.row.type]?.defaultSequence ?? 999;
      return sa - sb || a.index - b.index;
    })
    .map(({ row }) => row);

export const typeLabel = (type) => type?.label || type?.key || "";

export const typeSummary = (type) =>
  [typeLabel(type), ruleCategoryLabel(type?.category)]
    .filter(Boolean)
    .join(" · ");
